import {
  AfterViewInit,
  Component,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  AddCatalogItemsDialogComponent,
  AddCatalogItemsDialogData,
} from '@components/add-catalog-items-dialog/1.0';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { LoadingColor } from '@constants/loading/1.0';
import { MessageType } from '@constants/message/1.0';
import {
  ChangeSetItemType,
  FormattedChangesForItem,
  ItemChanges,
} from '@interfaces/change-sets/1.0';
import { Tag } from '@interfaces/tags/1.0';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validator,
} from '@angular/forms';
import { Observable, of, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TagType } from '@generated/tcea/models';
import { CatalogItemsService } from '@services/catalog-items/1.0/catalog-items.service';
import { DeepCloneService } from '@services/utilities/1.0/deep-clone.service';
import { GetService } from '@services/utilities/1.0/get.service';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { NgIf, AsyncPipe, DatePipe } from '@angular/common';
import { ButtonComponent } from '@components/button/1.0/button.component';
import { LoadingComponent } from '@components/loading/1.0/loading.component';
import { MessageComponent } from '@components/message/1.0/message.component';
import { ChangeSetNoticeComponent } from '@components/change-set-notice/change-set-notice.component';
@Component({
  selector: 'txp-catalog-item-tags',
  templateUrl: './catalog-item-tags.component.html',
  styleUrls: ['./catalog-item-tags.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CatalogItemTagsComponent),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => CatalogItemTagsComponent),
      multi: true,
    },
  ],
  standalone: true,
  imports: [
    NgIf,
    ButtonComponent,
    LoadingComponent,
    MessageComponent,
    ChangeSetNoticeComponent,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    TranslateModule,
    AsyncPipe,
    DatePipe,
  ],
})
export class CatalogItemTagsComponent
  implements ControlValueAccessor, OnInit, OnDestroy, Validator
{
  @ViewChild(MatSort) sort: MatSort;

  @Input() itemChanges: ItemChanges;
  @Input() selectedVersion: ChangeSetItemType;
  @Input() availableTags: Tag[];
  @Input() showError: boolean;
  @Input() loading: boolean;
  @Input() tagType: TagType;

  brandId: string;
  buttonSize = ButtonSize;
  buttonStyle = ButtonStyle;
  changeFn: any;
  loadingColor = LoadingColor;
  messageType = MessageType;
  form: UntypedFormGroup;
  tagButtonDisabled = false;
  touchedFn: any;
  filteredTagsList: Tag[] = [];
  isReadOnly$: Observable<boolean> = of(false);
  displayedColumns: string[] = ['name', 'type', 'startDate', 'endDate', 'actions'];
  dataSource: MatTableDataSource<Tag> = new MatTableDataSource<Tag>([]);

  private unsubscribe = new Subject<void>();

  constructor(
    private dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    private translateService: TranslateService,
    private catalogItemsService: CatalogItemsService,
  ) {
    this.form = this.formBuilder.group({
      tagIds: new UntypedFormControl([]),
    });

    this.isReadOnly$ = this.catalogItemsService.isReadOnlyItem$;
  }

  ngOnInit(): void {
    this.brandId = sessionStorage.getItem('brand_id');

    this.isReadOnly$.pipe(takeUntil(this.unsubscribe)).subscribe((isReadOnly) => {
      this.tagButtonDisabled = isReadOnly;
      if (isReadOnly) {
        this.form.disable();
      } else {
        this.form.enable();
      }
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  validate(control: AbstractControl): any {
    return this.form?.valid
      ? undefined
      : {
          invalidForm: {
            valid: false,
            message: this.translateService.instant('form.error.formInvalidMsg'),
          },
        };
  }

  writeValue(obj: any): void {
    const formControl = this.form.get('tagIds');
    formControl.setValue(obj, { emitEvent: false });
    this.form.markAsPristine();
  }

  registerOnChange(fn: any): void {
    this.changeFn = fn;
    this.form.valueChanges.pipe(takeUntil(this.unsubscribe)).subscribe((value) => {
      const formControl = this.form.get('tagIds');

      formControl.markAsDirty();
      fn(value?.tagIds || []);
    });
  }

  registerOnTouched(fn: any): void {
    this.touchedFn = fn;
  }

  hasFieldChanged(versionToCompare: string, itemChanges: FormattedChangesForItem): boolean {
    const sameVersion = versionToCompare === this.selectedVersion;
    const formForControl = this.form.get('tagIds');
    const marketingChanged =
      typeof GetService.get(itemChanges, 'tags.marketingTagIds') !== 'undefined';
    const ueTaxChanged =
      typeof GetService.get(itemChanges, 'tags.ueTaxCategoryIds') !== 'undefined';

    return marketingChanged || ueTaxChanged || (sameVersion && formForControl.dirty);
  }

  handleAddTag(): void {
    const formControl = this.form.get('tagIds');
    const tagAssociationList = formControl.value || [];
    const associatedIds = tagAssociationList.map((tag) => tag.id);
    let filteredTags: Tag[] = [];
    const availableTags = DeepCloneService.clone(this.availableTags);
    if (this.tagType) {
      filteredTags = availableTags.filter((tag) => tag.type === this.tagType);
    } else {
      // If no tagType is specified, we assume all tags are available
      filteredTags = availableTags.filter(
        (tag) =>
          tag.type !== TagType.Allergy &&
          tag.type !== TagType.Dietary &&
          tag.type !== TagType.UeTaxCategory,
      );

      filteredTags.forEach((tag) => {
        tag.type = this.translateService.instant(`catalog.tags.${tag.type.toLowerCase()}`);
      });
    }
    const hiddenTaxTags = tagAssociationList.filter((tag) => tag?.type === TagType.UeTaxCategory);
    const dialogData: AddCatalogItemsDialogData = {
      brandId: this.brandId,
      fullList: filteredTags.filter((i) => !associatedIds.includes(i.id)),
      selectedList: tagAssociationList.filter((tag) => tag?.type !== TagType.UeTaxCategory),
      itemTypeField: 'type',
      typeAlign: 'right',
      translations: {
        title: `${this.translateService.instant('catalog.tags.addTag')}`,
        type: this.translateService.instant('catalog.tags.entities'),
        group: this.translateService.instant('catalog.tags.title'),
        listTitle: this.translateService.instant('catalog.tags.title'),
      },
    };

    const dialogRef = this.dialog.open(AddCatalogItemsDialogComponent, {
      data: dialogData,
    });

    dialogRef.afterClosed().subscribe((result) => {
      // result undefined means it was cancelled
      if (result && result.items) {
        const newTagAssociationList = [].concat(result.items);
        let selectedList = [];
        newTagAssociationList.forEach((tag) => {
          let item = this.availableTags.find((availableTag) => availableTag.id === tag.id);
          selectedList.push(item);
        });

        formControl.setValue([...selectedList, ...hiddenTaxTags]);
      }
    });
  }

  onDelete(item): void {
    const formControl = this.form.get('tagIds');
    const tagAssociationList = DeepCloneService.clone(formControl.value || []);
    const index = tagAssociationList.findIndex((i) => i.id === item.id);

    tagAssociationList.splice(index, 1);

    formControl.setValue(tagAssociationList);
  }

  getFilteredTags(): Tag[] {
    const formControl = this.form.get('tagIds');
    const tagAssociationList = formControl.value || [];
    this.filteredTagsList = tagAssociationList.filter((tag) => tag?.type !== TagType.UeTaxCategory);
    this.dataSource.data = this.filteredTagsList;
    this.dataSource.sort = this.sort;
    return this.filteredTagsList;
  }
}
